import { useEffect, useMemo } from 'react';
import { Table, Spinner, Alert, Badge, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import { WaitEvent } from 'api/node_instances/operations/getNodeInstanceWaitEvents';
import { confirmDialog } from 'dialogs';
import { WaitEventsModel } from './WaitEventsModel';

interface Props {
  nodeInstanceId: number | string | null;
}

/**
 * Where to send someone asking "what is this wait actually waiting for?".
 *
 * Live Events is kind-blind, so the file-versus-process branching the two
 * per-kind instance pages needed is gone. The id is all the link takes, and it
 * is null when a flow-child path did not resolve to a known event.
 */
export const liveEventPath = (event: WaitEvent): string | null => (event.eventId === null ? null : `/automation/live_events?focus=${event.eventId}`);

const WaitEvents = observer(function WaitEvents({ nodeInstanceId }: Props) {
  const model = useMemo(() => new WaitEventsModel(nodeInstanceId), [nodeInstanceId]);

  useEffect(() => {
    void model.load();
  }, [model]);

  const handleRaiseAll = async () => {
    const pending = model.pending;
    if (pending.length === 0) return;
    const confirmed = await confirmDialog({
      title: `Raise ${pending.length} event${pending.length === 1 ? '' : 's'}`,
      body: (
        <>
          <p>
            Raise the following not-yet-raised event
            {pending.length === 1 ? '' : 's'}? Each creates a raised event instance.
          </p>
          <ul>
            {pending.map((e) => (
              <li key={e.eventId ?? `${e.source}-${e.zoneName}-${e.eventName}`}>
                {e.zoneName} | {e.eventName}
              </li>
            ))}
          </ul>
        </>
      ),
      confirmText: 'Raise all',
    });
    if (!confirmed) return;
    await model.raiseAll();
  };

  if (model.isLoading) return <Spinner animation="border" />;
  if (model.error) return <Alert variant="danger">{model.error}</Alert>;
  if (model.events.length === 0) {
    return <Alert variant="info">This instance is not waiting on any events.</Alert>;
  }

  return (
    <>
      <div className="d-flex justify-content-end gap-2 mb-2">
        <Button variant="outline-secondary" size="sm" onClick={() => model.refresh()} disabled={model.isBusy}>
          {model.isRefreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
        <Button variant="primary" size="sm" onClick={handleRaiseAll} disabled={model.isBusy || model.pending.length === 0}>
          {model.isRaisingAll ? 'Raising…' : `Raise all not-yet-raised${model.pending.length > 0 ? ` (${model.pending.length})` : ''}`}
        </Button>
      </div>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Event</th>
            <th>Zone</th>
            <th>Type</th>
            <th>Source</th>
            <th>Raised</th>
            <th>Auto-submit</th>
            <th>Auto-clear</th>
            <th>Precondition</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {model.events.map((event, index) => {
            const path = liveEventPath(event);
            return (
              <tr key={index}>
                <td>{path ? <Link to={path}>{event.eventName}</Link> : event.eventName}</td>
                <td>{event.zoneName}</td>
                <td>{event.eventType ?? '—'}</td>
                <td>
                  <Badge bg="light" text="dark">
                    {event.source === 'flow' ? 'Flow node' : 'Node'}
                  </Badge>
                </td>
                <td>
                  <Badge bg={event.raised ? 'success' : 'secondary'}>{event.raised ? 'Raised' : 'Not raised'}</Badge>
                </td>
                <td>{event.autoSubmit ? 'Yes' : 'No'}</td>
                <td>{event.autoClear ? 'Yes' : 'No'}</td>
                <td>{event.precondition ? <code>{event.precondition}</code> : '—'}</td>
                <td>
                  {model.canRaise(index) ? (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => model.raise(event)}
                      disabled={event.raised || model.isRowBusy(event) || model.isBusy}
                    >
                      {model.isRowBusy(event) ? 'Raising…' : 'Raise'}
                    </Button>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
});

export default WaitEvents;
